import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '../../../../utils';
import {
  ANIMAL_TYPE_OPTIONS,
  GENDER_OPTIONS,
  WEIGHT_OPTIONS,
  DEFAULT_OPTIONS,
  TARGET_COLUMNS,
  TARGET_FIELDS,
  buildCsv,
  createDefaultMapping,
  mapAll,
  parseCsv,
  type ConversionOptions,
  type MappingProfile,
  type ParsedCsv,
  type TargetField
} from './convertCsv';

export interface IProps {
  open: boolean;
  onClose: () => void;
  /** Receives the converted, target-schema CSV File ready for the validate flow. */
  onConvert: (file: File) => void;
}

const PREVIEW_LIMIT = 100;
const NONE = '__none__';

const ConvertCsvDialog: React.FC<IProps> = ({ open, onClose, onConvert }) => {
  const { t } = useTranslation();

  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [parseError, setParseError] = useState('');
  const [mapping, setMapping] = useState<MappingProfile | null>(null);
  const [options, setOptions] = useState<ConversionOptions>(DEFAULT_OPTIONS);

  const reset = () => {
    setFileName('');
    setParsed(null);
    setParseError('');
    setMapping(null);
    setOptions(DEFAULT_OPTIONS);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const loadFile = async (file: File) => {
    try {
      const text = await file.text();
      const result = parseCsv(text);
      if (result.headers.length === 0) {
        setParseError(t('f-import-convert-empty-error'));
        setParsed(null);
        setMapping(null);
        return;
      }
      setFileName(file.name);
      setParsed(result);
      setMapping(createDefaultMapping(result.headers));
      setParseError('');
    } catch (e) {
      setParseError(t('f-import-convert-parse-error', { error: (e as Error).message }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) loadFile(e.target.files[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) loadFile(e.dataTransfer.files[0]);
  };

  const setMappingField = (field: TargetField, value: string) => {
    setMapping((prev) => (prev ? { ...prev, [field]: value === NONE ? null : value } : prev));
  };

  const mapped = useMemo(() => {
    if (!parsed || !mapping) return null;
    return mapAll(parsed, mapping, options);
  }, [parsed, mapping, options]);

  const previewRows = mapped ? mapped.rows.slice(0, PREVIEW_LIMIT) : [];

  const handleConvert = () => {
    if (!parsed || !mapping || !mapped) return;
    if (mapped.rows.length === 0) {
      notifications.warning(t('f-import-convert-no-rows'));
      return;
    }

    const csv = buildCsv(mapped.rows);
    const baseName = fileName.replace(/\.csv$/i, '');
    const outName = `${baseName || 'import'}-converted.csv`;
    const file = new File([csv], outName, { type: 'text/csv' });

    onConvert(file);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('f-import-convert-title')}</DialogTitle>

      <DialogContent dividers>
        <p className="mb-4 text-sm text-gray-600">{t('f-import-convert-description')}</p>

        {/* File dropzone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="mb-6 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
        >
          <label className="w-full cursor-pointer">
            <input type="file" accept=".csv" hidden onChange={handleFileSelect} />
            <span className="text-sm text-gray-600">{fileName || t('f-import-convert-dropzone')}</span>
          </label>
        </div>

        {parseError && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {parseError}
          </div>
        )}

        {parsed && mapping && (
          <>
            {/* Constant options */}
            <div className="mb-6 rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700">{t('f-import-convert-options-title')}</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                <TextField
                  label={t('f-import-convert-opt-farm')}
                  value={options.farmNumber}
                  onChange={(e) => setOptions((o) => ({ ...o, farmNumber: e.target.value }))}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <TextField
                  select
                  label={t('f-import-convert-opt-type')}
                  value={options.animalType}
                  onChange={(e) => setOptions((o) => ({ ...o, animalType: e.target.value }))}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {ANIMAL_TYPE_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t('f-import-convert-opt-gender')}
                  value={options.animalGender}
                  onChange={(e) => setOptions((o) => ({ ...o, animalGender: e.target.value }))}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {GENDER_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t('f-import-convert-opt-weight')}
                  value={options.weightCategory}
                  onChange={(e) => setOptions((o) => ({ ...o, weightCategory: e.target.value }))}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {WEIGHT_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <div className="sm:col-span-2">
                  <TextField
                    label={t('f-import-convert-opt-notes')}
                    value={options.notes}
                    onChange={(e) => setOptions((o) => ({ ...o, notes: e.target.value }))}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* Column mapping */}
            <div className="mb-6 rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700">{t('f-import-convert-mapping-title')}</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {TARGET_FIELDS.map((field) => (
                  <TextField
                    key={field}
                    select
                    label={field}
                    value={mapping[field] ?? NONE}
                    onChange={(e) => setMappingField(field, e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    <MenuItem value={NONE}>{t('f-import-convert-leave-empty')}</MenuItem>
                    {parsed.headers.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </div>
            </div>

            {/* Live preview */}
            <div className="rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700">{t('f-import-convert-preview-title')}</h3>
                {mapped && (
                  <span className="text-xs text-gray-500">
                    {t('f-import-convert-preview-count', {
                      total: mapped.rows.length,
                      shown: previewRows.length
                    })}
                    {mapped.skipped > 0 && (
                      <span className="ml-2 text-red-600">
                        {t('f-import-convert-preview-skipped', {
                          skipped: mapped.skipped,
                          error: mapped.firstError
                        })}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-200 bg-gray-50">
                    <tr>
                      {TARGET_COLUMNS.map((col) => (
                        <th key={col} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-gray-700">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {TARGET_COLUMNS.map((col) => (
                          <td key={col} className="whitespace-nowrap px-3 py-2 text-gray-700">
                            {row[col]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {previewRows.length === 0 && (
                      <tr>
                        <td colSpan={TARGET_COLUMNS.length} className="px-3 py-6 text-center text-gray-400">
                          {t('f-import-convert-preview-empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('f-import-convert-cancel')}</Button>
        <Button
          onClick={handleConvert}
          disabled={!mapped || mapped.rows.length === 0}
          variant="contained"
          color="primary"
        >
          {t('f-import-convert-submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertCsvDialog;
